import React from "react";
import { useNavigate } from "react-router-dom";
import { type IconName } from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useToast } from "../../../contexts/ToastContext";
import { useSendPaymentRemindersMutation } from "../../../store/api/paymentsApi";
import { useSendRenewalRemindersMutation } from "../../../store/api/renewalRemindersApi";
import {
	arrayToCsv,
	downloadCsv,
	formatCurrencyForCsv,
	formatDateForCsv,
} from "../../../utils/csvExport";

interface QuickActionsProps {
	onAddTenant?: () => void;
	tenants?: Array<{
		id: string;
		name: string;
		email: string;
		phone: string;
		property: string;
		unit: string;
		status: string;
		paymentStatus: string;
		leaseStart: string;
		leaseEnd: string;
		monthlyRent: number;
	}>;
}

const QuickActions: React.FC<QuickActionsProps> = ({
	onAddTenant,
	tenants = [],
}) => {
	const navigate = useNavigate();
	const { success, error: showError } = useToast();
	const [sendRenewalReminders, { isLoading: isSendingRenewal }] =
		useSendRenewalRemindersMutation();
	const [sendPaymentReminders, { isLoading: isSendingPayment }] =
		useSendPaymentRemindersMutation();

	const handleGenerateReports = () => {
		if (!tenants || tenants.length === 0) {
			showError("No tenant data available to generate reports");
			return;
		}

		// Generate comprehensive tenant report
		const csvData = tenants.map((tenant) => ({
			"Tenant Name": tenant.name || "",
			Email: tenant.email || "",
			Phone: tenant.phone || "",
			Property: tenant.property || "",
			Unit: tenant.unit || "",
			Status: tenant.status || "",
			"Payment Status": tenant.paymentStatus || "",
			"Lease Start": formatDateForCsv(tenant.leaseStart),
			"Lease End": formatDateForCsv(tenant.leaseEnd),
			"Monthly Rent": formatCurrencyForCsv(tenant.monthlyRent),
		}));

		const csvContent = arrayToCsv(csvData, [
			"Tenant Name",
			"Email",
			"Phone",
			"Property",
			"Unit",
			"Status",
			"Payment Status",
			"Lease Start",
			"Lease End",
			"Monthly Rent",
		]);

		downloadCsv(csvContent, "tenant-reports");
		success("Tenant report generated and downloaded successfully");
	};

	const handlePaymentReminders = async () => {
		try {
			const result = await sendPaymentReminders().unwrap();
			if (result.success) {
				const { emails_sent, notifications_created, total_payments } =
					result.result;
				success(
					`Payment reminders sent successfully! ${emails_sent} emails sent, ${notifications_created} notifications created for ${total_payments} payments.`,
				);
			}
		} catch (error: any) {
			showError(
				error?.data?.detail ||
					"Failed to send payment reminders. Please try again.",
			);
		}
	};

	const handleExportData = () => {
		if (!tenants || tenants.length === 0) {
			showError("No tenant data available to export");
			return;
		}

		// Export all tenant data
		const csvData = tenants.map((tenant) => ({
			"Tenant ID": tenant.id || "",
			"Tenant Name": tenant.name || "",
			Email: tenant.email || "",
			Phone: tenant.phone || "",
			Property: tenant.property || "",
			Unit: tenant.unit || "",
			Status: tenant.status || "",
			"Payment Status": tenant.paymentStatus || "",
			"Lease Start": formatDateForCsv(tenant.leaseStart),
			"Lease End": formatDateForCsv(tenant.leaseEnd),
			"Monthly Rent": formatCurrencyForCsv(tenant.monthlyRent),
		}));

		const csvContent = arrayToCsv(csvData, [
			"Tenant ID",
			"Tenant Name",
			"Email",
			"Phone",
			"Property",
			"Unit",
			"Status",
			"Payment Status",
			"Lease Start",
			"Lease End",
			"Monthly Rent",
		]);

		downloadCsv(csvContent, "tenant-data-export");
		success("Tenant data exported successfully");
	};

	const actions: Array<{
		label: string;
		icon: IconName;
		variant: "default" | "outline";
		description: string;
		onClick: () => void;
		disabled?: boolean;
	}> = [
		{
			label: "Add New Tenant",
			icon: "UserPlus" as IconName,
			variant: "default" as const,
			description: "Register a new tenant",
			onClick: () => {
				if (onAddTenant) {
					onAddTenant();
				}
			},
		},
		{
			label: "Send Bulk Email",
			icon: "Mail" as IconName,
			variant: "outline" as const,
			description: "Email multiple tenants",
			onClick: () => navigate("/messages"),
		},
		{
			label: "Generate Reports",
			icon: "FileText" as IconName,
			variant: "outline" as const,
			description: "Create tenant reports",
			onClick: handleGenerateReports,
		},
		{
			label: "Lease Renewals",
			icon: "RefreshCw" as IconName,
			variant: "outline" as const,
			description: "Manage lease renewals",
			onClick: () => navigate("/lease-management"),
		},
		{
			label: "Payment Reminders",
			icon: "Bell" as IconName,
			variant: "outline" as const,
			description: "Send payment reminders",
			onClick: handlePaymentReminders,
			disabled: isSendingPayment,
		},
		{
			label: "Export Data",
			icon: "Download" as IconName,
			variant: "outline" as const,
			description: "Export tenant data",
			onClick: handleExportData,
		},
	];

	return (
		<div className="bg-card border border-border rounded-lg p-6">
			<h3 className="text-lg font-semibold text-foreground mb-4">
				Quick Actions
			</h3>

			<div className="space-y-3">
				{actions.map((action, index) => (
					<Button
						key={index}
						variant={action.variant}
						size="sm"
						fullWidth
						iconName={action.icon}
						iconPosition="left"
						className="justify-start"
						onClick={action.onClick}
						disabled={action.disabled}
					>
						<div className="text-left">
							<div className="font-medium">{action.label}</div>
							<div className="text-xs opacity-75">{action.description}</div>
						</div>
					</Button>
				))}
			</div>
		</div>
	);
};

export default QuickActions;
